import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Loader2, Edit, Trash2, Share2, ExternalLink, Kanban, Users, Briefcase } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import RecruiterLayout from '@/components/RecruiterLayout';
import JobForm from '@/components/JobForm';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { usePermissions } from '@/hooks/usePermissions';
import { DataTable, DataTableColumn, DataTableAction, DataTableFilter, MassAction } from '@/components/ui/data-table';

interface Job {
  id: string;
  title: string;
  status: string;
  location: string | null;
  job_type: string | null;
  work_mode: string | null;
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string | null;
  created_at: string;
  updated_at: string | null;
  published_at: string | null;
  clients?: { name: string } | null;
  candidate_applications?: { id: string }[];
  assigned_to?: string | null;
  assignedRecruiter?: { full_name: string } | null;
}

const JobManagement = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [previewJobId, setPreviewJobId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const { toast } = useToast();
  const navigate = useNavigate();
  const { can } = usePermissions();

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select(`
          *,
          clients (name),
          candidate_applications (id),
          assignedRecruiter:app_users!jobs_assigned_to_fkey (full_name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setJobs(data || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (job: Job) => {
    if (!confirm('Are you sure you want to delete this job?')) return;
    
    try {
      const { error } = await supabase.from('jobs').delete().eq('id', job.id);
      if (error) throw error;
      toast({ title: 'Job deleted successfully' });
      fetchJobs();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleBulkDelete = async (selectedJobs: Job[]) => {
    if (!confirm(`Are you sure you want to delete ${selectedJobs.length} jobs?`)) return;
    
    try {
      const ids = selectedJobs.map(j => j.id);
      const { error } = await supabase.from('jobs').delete().in('id', ids);
      if (error) throw error;
      toast({ title: `${selectedJobs.length} jobs deleted successfully` });
      fetchJobs();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleEdit = (job: Job) => {
    setSelectedJob(job);
    setFormOpen(true);
  };

  const handleCreate = () => {
    setSelectedJob(null);
    setFormOpen(true);
  };

  const copyPublicLink = (job: Job) => {
    const link = `${window.location.origin}/jobs/${job.id}`;
    navigator.clipboard.writeText(link);
    toast({ title: 'Link copied!', description: 'Public job link copied to clipboard' });
  };

  // Filter data by status
  const filteredJobs = statusFilter === 'all' 
    ? jobs 
    : jobs.filter(j => j.status === statusFilter);

  const columns: DataTableColumn<Job>[] = [
    {
      id: 'title',
      header: 'Title',
      accessorKey: 'title',
      sortable: true,
    },
    {
      id: 'client',
      header: 'Client',
      accessorKey: 'clients.name',
      cell: (row) => row.clients?.name || '-',
      sortable: true,
    },
    {
      id: 'location',
      header: 'Location',
      accessorKey: 'location',
      cell: (row) => row.location || '-',
      sortable: true,
    },
    {
      id: 'type',
      header: 'Type',
      accessorKey: 'job_type',
      cell: (row) => row.job_type?.replace('_', ' ') || '-',
      sortable: true,
    },
    {
      id: 'recruiter',
      header: 'Recruiter',
      accessorFn: (row) => row.assignedRecruiter?.full_name || '',
      cell: (row) => row.assignedRecruiter?.full_name || '-',
      sortable: true,
    },
    {
      id: 'work_mode',
      header: 'Work Mode',
      accessorKey: 'work_mode',
      cell: (row) => row.work_mode?.replace('_', ' ') || '-',
      sortable: true,
      defaultVisible: false,
    },
    {
      id: 'salary',
      header: 'Salary Range',
      accessorKey: 'salary_min',
      cell: (row) => {
        if (!row.salary_min) return '-';
        return `${row.salary_min?.toLocaleString()}${row.salary_max ? ` - ${row.salary_max.toLocaleString()}` : ''} ${row.salary_currency || ''}`;
      },
      sortable: true,
      defaultVisible: false,
    },
    {
      id: 'candidates',
      header: 'Candidates',
      accessorFn: (row) => row.candidate_applications?.length || 0,
      cell: (row) => (
        <Badge variant="outline">{row.candidate_applications?.length || 0}</Badge>
      ),
      sortable: true,
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      cell: (row) => (
        <Badge variant={row.status === 'published' ? 'default' : 'secondary'}>
          {row.status}
        </Badge>
      ),
      sortable: true,
    },
    {
      id: 'created_at',
      header: 'Created',
      accessorKey: 'created_at',
      cell: (row) => new Date(row.created_at).toLocaleDateString(),
      sortable: true,
      defaultVisible: false,
    },
    {
      id: 'published_at',
      header: 'Published',
      accessorKey: 'published_at',
      cell: (row) => row.published_at ? new Date(row.published_at).toLocaleDateString() : '-',
      sortable: true,
      defaultVisible: false,
    },
  ];

  const filters: DataTableFilter[] = [
    {
      id: 'status',
      label: 'Status',
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { label: 'All Statuses', value: 'all' },
        { label: 'Draft', value: 'draft' },
        { label: 'Published', value: 'published' },
        { label: 'Closed', value: 'closed' },
      ],
    },
  ];

  const actions: DataTableAction<Job>[] = [
    {
      label: 'Open Kanban',
      icon: <Kanban className="h-4 w-4" />,
      onClick: (job) => navigate(`/recruiter/jobs/${job.id}/kanban`),
    },
    {
      label: 'Share Link',
      icon: <Share2 className="h-4 w-4" />,
      onClick: copyPublicLink,
    },
    {
      label: 'Preview',
      icon: <ExternalLink className="h-4 w-4" />,
      onClick: (job) => setPreviewJobId(job.id),
    },
    {
      label: 'Edit',
      icon: <Edit className="h-4 w-4" />,
      onClick: handleEdit,
      show: () => can.update('jobs'),
    },
    {
      label: 'Delete',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: handleDelete,
      variant: 'destructive',
      show: () => can.delete('jobs'),
    },
  ];

  const massActions: MassAction<Job>[] = [
    {
      label: 'Delete Selected',
      icon: <Trash2 className="h-4 w-4 mr-2" />,
      onClick: handleBulkDelete,
      variant: 'destructive',
    },
  ];

  return (
    <RecruiterLayout>
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">Job Management</h1>
            <p className="text-muted-foreground">Create and manage job postings</p>
          </div>
          {can.create('jobs') && (
            <Button onClick={handleCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Create Job
            </Button>
          )}
        </div>

        <DataTable
          data={filteredJobs}
          columns={columns}
          keyField="id"
          searchPlaceholder="Search jobs..."
          searchFields={['title', 'clients.name', 'location']}
          filters={filters}
          actions={actions}
          massActions={can.delete('jobs') ? massActions : []}
          onRowClick={(job) => navigate(`/recruiter/jobs/${job.id}/kanban`)}
          titleField="title"
          subtitleField="client"
          emptyMessage="No jobs found"
          emptyIcon={<Briefcase className="h-12 w-12" />}
          loading={loading}
        />

        <JobForm
          job={selectedJob}
          open={formOpen}
          onOpenChange={setFormOpen}
          onSuccess={() => {
            fetchJobs();
            setFormOpen(false);
          }}
        />

        {/* Preview Dialog */}
        <Dialog open={!!previewJobId} onOpenChange={() => setPreviewJobId(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>Job Preview</DialogTitle>
              <DialogDescription>
                This is how candidates will see this job posting
              </DialogDescription>
            </DialogHeader>
            <div className="w-full h-[70vh]">
              <iframe
                src={`/jobs/${previewJobId}`}
                className="w-full h-full border-0 rounded-lg"
                title="Job Preview"
              />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </RecruiterLayout>
  );
};

export default JobManagement;
